import React from 'react';
import { render } from 'react-dom';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import { Router as BrowserRouter } from 'react-router-dom';
import localforage from 'localforage';
import Router from './containers/Router';
import history from './utils/history';
import { DragDropContextProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import reducer from './reducers';
import { init, setAppInfo, setLoading, getAppList } from './actions/app';
import { initializeAuth, setAuthenticated } from './actions/auth';
import autoStartGdl from './middleware/autoStartGdl';
import apiMiddleware from './middleware/apiMiddleware';
import timeOnTaskLogger from './middleware/timeOnTaskMiddleware';
import questionLoader from './middleware/researchMiddleware';
import gameMiddleware from './middleware/gameMiddleware';
import unityCommander from './middleware/unityCommander';
import autoSaveMiddleware from './middleware/autoSaveMiddleware';
import logoutMiddleware from './middleware/logoutMiddleware';
import Console from './utils/console';
import AppErrorBoundary from './components/Error/AppErrorBoundary';
import './utils/fontawesome';
// import 'bootstrap/dist/css/bootstrap.min.css';
import './index.scss';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

// configure sentry
// if (
//   process.env.NODE_ENV === 'production' &&
//   process.env.REACT_APP_SENTRY_RELEASE
// ) {
//   console.log('Initialized Sentry');
//   Sentry.init({
//     dsn:
//       'https://1927c46ff77349099f0e5580c740cb90@o1163324.ingest.sentry.io/6396782',
//     release: process.env.REACT_APP_SENTRY_RELEASE,

//     integrations: [new BrowserTracing()],

//     // Set tracesSampleRate to 1.0 to capture 100%
//     // of transactions for performance monitoring.
//     // Currently it's 0.0. because we are not using
//     // Sentry's performance monitoring feature at this time.
//     tracesSampleRate: 0.0,
//   });
// }

// configure localforage
localforage.config({
  name: 'wbapp',
  description: 'WhiteBox Learning application preferences',
});

// create redux store and middleware
const sentryReduxEnhancer = Sentry.createReduxEnhancer({});
const composeEnhancers =
  (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      trace: true,
      traceLimit: 25,
    })) ||
  compose;
const store = createStore(
  reducer,
  composeEnhancers(
    applyMiddleware(
      thunk,
      apiMiddleware,
      autoStartGdl,
      timeOnTaskLogger,
      gameMiddleware,
      questionLoader,
      unityCommander,
      autoSaveMiddleware,
      logoutMiddleware
    ),
    sentryReduxEnhancer
  )
);

// authenticate and initialize application
store.dispatch(initializeAuth());

// attach a cli console
window._wbl = () => new Console(store);

// render the React root component
render(
  <AppErrorBoundary>
    <DragDropContextProvider backend={HTML5Backend}>
      <Provider store={store}>
        <BrowserRouter history={history}>
          <Router />
        </BrowserRouter>
      </Provider>
    </DragDropContextProvider>
  </AppErrorBoundary>,
  document.getElementById('root')
);

// hot reload for development
if (module.hot) {
  // reducer changes
  module.hot.accept('./reducers/', () => {
    const nextReducer = require('./reducers/index').default;
    store.replaceReducer(nextReducer);
  });

  // component changes
  module.hot.accept('./containers/Router', () => {
    const NextRouter = require('./containers/Router').default;
    render(
      <AppErrorBoundary>
        <DragDropContextProvider backend={HTML5Backend}>
          <Provider store={store}>
            <BrowserRouter history={history}>
              <NextRouter />
            </BrowserRouter>
          </Provider>
        </DragDropContextProvider>
      </AppErrorBoundary>,
      document.getElementById('root')
    );
  });
}
